import React, { useCallback, useContext, useMemo, useState } from 'react';
import classNames from 'classnames';
import { ShareableLinkMainInfo } from 'models/ShareableLink';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import api from '../../api';
import { useTranslation } from 'react-i18next';
import { Field, Form } from 'react-final-form';
import PasswordField from 'components/PasswordField';
import validations from 'validations';
import { Button } from 'ncoded-component-library';

import './ShareableLinkPassword.styles.scss';

type ShareableLinkPasswordProps = {
  className?: string;
  shareableLinkId: string;
  mainInfo?: ShareableLinkMainInfo;
  updateAuthInfo: (
    newMainInfo: ShareableLinkMainInfo,
    newIsPasswordConfirmed?: boolean,
  ) => void;
};

const ShareableLinkPassword: React.FC<ShareableLinkPasswordProps> = (props) => {
  const {
    className,
    shareableLinkId,
    mainInfo: { email, requestedPasswordAt },
    updateAuthInfo,
  } = props;

  const [hasRequestOrReminderSent, setHasRequestOrReminderSent] =
    useState(false);

  const hasAlreadyRequestedAccess = useMemo(
    () => !!requestedPasswordAt,
    [requestedPasswordAt],
  );

  const classes = classNames('shareable-link-password', className);

  const { popSuccess, popServerError } = useContext(PopNotificationsContext);

  const { t } = useTranslation();

  const onSendReminder = useCallback(async () => {
    try {
      setHasRequestOrReminderSent(true);
      await api.shareableLinks.requestPasswordReminder(shareableLinkId, email);
      popSuccess(t('yourPasswordRequestubmittedSuccessfully'));
    } catch (e) {
      popServerError(e);
      setHasRequestOrReminderSent(false);
    }
  }, [shareableLinkId, email, popSuccess, t, popServerError]);

  const onRequestPassword = useCallback(async () => {
    try {
      setHasRequestOrReminderSent(true);
      await api.shareableLinks.requestPassword(shareableLinkId, email);
      popSuccess(t('yourPasswordRequestubmittedSuccessfully'));
    } catch (e) {
      popServerError(e);
      setHasRequestOrReminderSent(false);
    }
  }, [shareableLinkId, email, popSuccess, t, popServerError]);

  const onSubmit = useCallback(
    async (values) => {
      const { password } = values;
      try {
        const { data } = await api.shareableLinks.confirmPassword(
          shareableLinkId,
          email,
          password,
        );

        updateAuthInfo(data, true);
      } catch (e) {
        popServerError(e);
      }
    },
    [shareableLinkId, email, updateAuthInfo, popServerError],
  );

  return (
    <div className={classes}>
      <Form
        onSubmit={onSubmit}
        render={({ handleSubmit, submitting }) => (
          <form onSubmit={handleSubmit}>
            <h2>{t('passwordSetSellsideAdministrator')}</h2>

            <div>
              <Field
                name="password"
                component={PasswordField}
                label={t('password')}
                required
                validate={validations.required()}
                withoutStrengthIndicator
                autoComplete="new-password"
              />
              <span />
            </div>

            <div className="actions">
              <Button type="submit" disabled={submitting}>
                {t('submit')}
              </Button>

              <Button
                variant="link"
                disabled={hasRequestOrReminderSent}
                onClick={
                  hasAlreadyRequestedAccess ? onSendReminder : onRequestPassword
                }
              >
                {t('requestAccess')}
              </Button>
            </div>
          </form>
        )}
      />
    </div>
  );
};

export default ShareableLinkPassword;
